
import React from 'react';
import { format } from 'date-fns';
import { Clock, User, Eye } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SessionBookingWithMentor } from '@/utils/menteeBookingsUtils';

interface MenteeDayDetailModalProps {
  date: Date | null;
  isOpen: boolean;
  onClose: () => void;
  bookings: SessionBookingWithMentor[];
  onBookingClick: (booking: SessionBookingWithMentor) => void;
}

const MenteeDayDetailModal = ({ 
  date, 
  isOpen, 
  onClose, 
  bookings, 
  onBookingClick 
}: MenteeDayDetailModalProps) => {
  if (!date) return null;

  // Generate hourly slots from 7 AM to 9 PM
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 7; hour <= 21; hour++) {
      const time = `${hour.toString().padStart(2, '0')}:00`;
      slots.push(time);
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Helper function to convert time string to minutes for positioning
  const timeToMinutes = (timeStr: string) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Helper function to check if a booking overlaps with a time slot
  const getBookingForSlot = (time: string) => {
    return bookings.find(booking => {
      const slotTime = timeToMinutes(time);
      const startTime = timeToMinutes(booking.start_time);
      const endTime = timeToMinutes(booking.end_time);
      return slotTime >= startTime && slotTime < endTime;
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-hidden flex flex-col !rounded-none">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-emu-blue" />
            {format(date, 'EEEE, MMMM d, yyyy')}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4">
          <div className="space-y-1">
            {timeSlots.map((time) => {
              const booking = getBookingForSlot(time);
              
              // All slots are white, with blue highlight for booked appointments
              let bgColor = 'bg-white';
              let textColor = 'text-gray-600';
              
              if (booking) {
                bgColor = 'bg-blue-100';
                textColor = 'text-blue-800';
              }

              return (
                <div
                  key={time}
                  className={`flex items-center gap-3 p-3 h-12 border border-gray-200 ${bgColor} ${textColor}`}
                >
                  <div className="w-16 text-sm font-medium flex-shrink-0">
                    {time}
                  </div>

                  <div className="flex-1 min-w-0">
                    {booking ? (
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <User className="h-3 w-3 flex-shrink-0" />
                          <span className="text-sm truncate">
                            {booking.mentor.first_name} {booking.mentor.last_name}
                          </span>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          onClick={() => onBookingClick(booking)}
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          View Details
                        </Button>
                      </div>
                    ) : (
                      <span className="text-sm">Free</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="border-t pt-4 mt-4">
          <div className="flex flex-wrap gap-4 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-100 border border-blue-200"></div>
              <span className="text-gray-600">Appointment Scheduled</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-white border border-gray-200"></div>
              <span className="text-gray-600">Free Time</span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MenteeDayDetailModal;
